function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';
import styles from "./InlineControl.module.scss";
import { Typography } from "../../Typography";
import { preventBadNumberInputKeys, preventBadNumberInputValues } from "../../../input-management";
import { TableList } from "../TableList";
import { Tooltip } from "../../Tooltip";
export const InlineInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    value,
    error,
    errorText,
    errorTextInTooltip,
    inputProps,
    testid,
    className,
    onChange,
    onFocus,
    onBlur,
    onSave
  } = props;
  const inputRef = useRef(null);

  // workaround to reuse the same ref
  useImperativeHandle(ref, () => {
    var _inputRef$current;
    return (_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 ? _inputRef$current : new HTMLInputElement();
  });

  // The initial value when focusing on the input.
  const [pristineValue, setPristineValue] = useState(value);

  // Logic for blur on {enter} and undo on {escape}.
  useEffect(() => {
    const inputEl = inputRef.current;
    const onKeyDown = event => {
      if (!inputEl || document.activeElement !== inputEl) {
        return;
      }
      if (event.key === 'Enter') {
        inputEl.blur();
      } else if (event.key === 'Escape') {
        onChange === null || onChange === void 0 || onChange(pristineValue);
        inputEl.blur();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onChange, pristineValue]);

  // Save mechanism
  const [valueToSave, setValueToSave] = useState(null);

  // should be called after validation process has completed
  useEffect(() => {
    if (valueToSave !== null) {
      setValueToSave(null);
      if (!error && valueToSave !== pristineValue) {
        setPristineValue(valueToSave);
        onSave === null || onSave === void 0 || onSave(valueToSave);
      }
    }
  }, [error, onSave, pristineValue, valueToSave]);
  const isNumberInput = inputProps && inputProps.type === 'number';
  return /*#__PURE__*/React.createElement(Tooltip, {
    title: errorTextInTooltip && errorText ? /*#__PURE__*/React.createElement("div", {
      "data-testid": testid && "".concat(testid, "-error")
    }, errorText) : '',
    interactive: true,
    disableArrow: true,
    disableHoverListener: true,
    disableTouchListener: true
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.root, className)
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.frame, styles.absolutePosition, TableList.classes.showOnRowHover)
  }), /*#__PURE__*/React.createElement(Typography, _extends({}, inputProps, {
    ellipsis: true,
    as: "input",
    ref: inputRef,
    weight: "normal",
    className: cn(styles.formElement, styles.absolutePosition, {
      [styles.error]: error
    }),
    value: value,
    "data-testid": testid,
    "data-test-error": error || undefined,
    tooltipProps: {
      disableFocusListener: true
    },
    onChange: function (_onChange) {
      function onChange(_x) {
        return _onChange.apply(this, arguments);
      }
      onChange.toString = function () {
        return _onChange.toString();
      };
      return onChange;
    }(e => {
      const value = e.currentTarget.value;
      const inFocus = document.activeElement === e.currentTarget;
      isNumberInput && preventBadNumberInputValues(e);
      onChange === null || onChange === void 0 || onChange(value);
      if (!inFocus) {
        setValueToSave(value.trim());
      }
    }),
    onKeyDown: isNumberInput ? e => preventBadNumberInputKeys(e, inputProps.min != null && +inputProps.min >= 0) : undefined,
    onFocus: function (_onFocus) {
      function onFocus(_x2) {
        return _onFocus.apply(this, arguments);
      }
      onFocus.toString = function () {
        return _onFocus.toString();
      };
      return onFocus;
    }(e => {
      onFocus === null || onFocus === void 0 || onFocus(e);
    }),
    onBlur: function (_onBlur) {
      function onBlur(_x3) {
        return _onBlur.apply(this, arguments);
      }
      onBlur.toString = function () {
        return _onBlur.toString();
      };
      return onBlur;
    }(e => {
      onBlur === null || onBlur === void 0 || onBlur(e);
      setValueToSave(e.currentTarget.value.trim());
    })
  })), errorText && !errorTextInTooltip && /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph3",
    skin: "error",
    className: styles.errorText,
    "data-testid": testid && "".concat(testid, "-error")
  }, errorText)));
});